// The help drawer, the Help button that opens it, and the walkthrough that's
// running. Content is in help.tsx; state in guide-state.ts.

import type { ComponentChildren } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { Button, Drawer, Icon, Tour } from "../../../ui/src/index.ts";
import type { HubSummary } from "../domain/types.ts";
import { closeHelp, endTour, openHelp, startTour, stepTour, type TourId } from "./guide-state.ts";
import { PAGE_HELP, TOURS, type HelpContext, type HelpPage } from "./help.tsx";
import type { HubId } from "../../../src/exchange/curator-api.ts";
import { href, type ParsedRoute, type Route } from "./router.ts";
import { useStore } from "./store.ts";

const HELP_PAGES = new Set<string>(Object.keys(PAGE_HELP));
const isHelpPage = (name: string): name is HelpPage => HELP_PAGES.has(name);
/** The page whose help and walkthrough apply, or null on Help itself and while loading. */
export const helpPage = (route: ParsedRoute): HelpPage | null => (isHelpPage(route.name) ? route.name : null);

export function useHelpContext(hub: HubSummary): HelpContext {
  const sample = useStore((s) => s.sample);
  return { hub: hub.hub.id, floorPerMillion: sample.deliveryFeePerMillion + sample.inputsPerMillion };
}

/** A page header's actions, ending with the Help button (an icon). */
export function HeaderActions({ children }: { children?: ComponentChildren }) {
  return <div class="st-actions">{children}<Button icon size="sm" variant="ghost" class="st-help" aria-label="Help" title="Help for this page" onClick={openHelp}><Icon name="help" size={20} /></Button></div>;
}

/** Where a walkthrough runs. The Studio tour runs anywhere, so it starts on the Hub overview. */
export function tourRoute(id: TourId, hub: HubId): Route {
  const page = TOURS[id].page;
  switch (page) {
    case "any": case "overview": return { name: "overview", hub };
    case "questions": case "golden": case "pricing": case "reputation": return { name: page, hub };
    case "demand": return { name: "demand", hub, group: "declined", days: 30, search: "" };
    case "question": return { name: "questions", hub };
    default: return page satisfies never;
  }
}

export function HelpDrawer({ route, hub }: { route: ParsedRoute; hub: HubSummary }) {
  const open = useStore((s) => s.helpOpen);
  const context = useHelpContext(hub);
  const page = helpPage(route);
  const help = page ? PAGE_HELP[page] : null;
  const tour = help?.tour;
  return <Drawer open={open && !!help} title={help ? `Help: ${help.title}` : "Help"} onClose={closeHelp}
    footer={<>
      {tour && <Button size="sm" onClick={() => startTour(tour)}><Icon name="tour" size={16} />Take the walkthrough</Button>}
      <Button size="sm" variant="ghost" href={href({ name: "help", hub: context.hub, item: "" })} onClick={closeHelp}>All help and FAQ</Button>
    </>}>
    {help?.sections(context).map((s) => <section key={s.title}><h3>{s.title}</h3>{s.body}</section>)}
  </Drawer>;
}

/** Runs the walkthrough that's on, and offers one when walkthroughs are on:
 *  the Studio tour first, then the page's own, at most one per page visit. */
export function GuidedTour({ route }: { route: ParsedRoute }) {
  const tour = useStore((s) => s.tour);
  const on = useStore((s) => s.toursOn);
  const seen = useStore((s) => s.seenTours);
  const helpOpen = useStore((s) => s.helpOpen);
  const page = helpPage(route);
  const offered = useRef<HelpPage | null>(null);

  useEffect(() => {
    if (!on || tour || helpOpen || !page || offered.current === page) return;
    const next = seen.includes("studio") ? PAGE_HELP[page].tour : "studio";
    if (!next || seen.includes(next)) return;
    const timer = setTimeout(() => { offered.current = page; startTour(next); }, 600);
    return () => clearTimeout(timer);
  }, [on, tour, helpOpen, page, seen.length]);

  // A page's walkthrough ends when you leave the page.
  useEffect(() => {
    if (page !== offered.current) offered.current = null;
    const running = tour ? TOURS[tour.id].page : null;
    if (running && running !== "any" && running !== page) endTour();
  }, [page]);

  if (!tour) return null;
  const walkthrough = TOURS[tour.id];
  if (walkthrough.page !== "any" && walkthrough.page !== page) return null;
  return <Tour title={walkthrough.title} steps={walkthrough.steps} step={tour.step} onStep={stepTour} onClose={endTour} />;
}
